import { api } from "./api";
import { fbqTrack } from "./fbpixel";
import { readCapiClientParams } from "./capiClient";
import { getCurrentUser } from "./firebase";
import { isPaidTraffic } from "./paidTraffic";

/**
 * Client-side Meta CAPI helper. Pairs a server CAPI POST with a
 * browser-side Pixel `fbq('track', …, { eventID })` call so Meta
 * dedupes the two streams.
 *
 * Two important rules:
 *  1. The same `eventId` must be used on BOTH sides (server + browser)
 *     for the same occurrence. Otherwise you double-count.
 *  2. For purchase-time events (Purchase / StartTrial / Subscribe), use
 *     the Paddle `transaction_id` as the event_id so the OTHER backend's
 *     webhook-side CAPI also dedupes against this one.
 *
 * `fbp` / `fbc` / `clientIpAddress` come from Meta's official client
 * SDK (`meta-capi-param-builder-clientjs`) initialised at app boot —
 * the same SDK that backs the server. The browser's IPv6 (if any) and
 * in-app-browser click-ID fallbacks land here. The Worker still falls
 * back to its own Cookie-header resolution when these are missing.
 */

export type CapiEventName =
  // ── Meta standard events ──
  | "ViewContent"
  | "Lead"
  | "CompleteRegistration"
  | "InitiateCheckout"
  | "StartTrial"
  | "Subscribe"
  | "Purchase"
  // ── Custom events ──
  // Sent via the Pixel's `trackCustom` (auto-routed in fbpixel.ts) and
  // CAPI with the verbatim name. To optimise a campaign toward one,
  // create a Custom Conversion on this event name in Events Manager.
  //   BetterMeIntent — result-screen "I want to see a better me" tap.
  //   QuizEngaged   — top-of-funnel intent: visitor answered the
  //                   doomscrolling question (proves they're past
  //                   the curiosity tap and committing to the quiz).
  | "BetterMeIntent"
  | "QuizEngaged";

interface FireOpts {
  eventName: CapiEventName;
  /** Override the auto-generated UUID — e.g. pass Paddle txId for Purchase. */
  eventId?: string;
  /** Raw PII — the Worker normalises + hashes via Meta's SDK. */
  email?: string;
  gender?: "male" | "female";
  phone?: string;
  firstName?: string;
  lastName?: string;
  /** Stable identifier hashed server-side as external_id. We default
   *  to the Firebase uid when a user is signed in. */
  externalId?: string;
  /** Standard-event custom data. */
  value?: number;
  currency?: string;
  contentName?: string;
  contentCategory?: string;
  /** Anything else accepted by Meta's custom_data. */
  extraCustom?: Record<string, unknown>;
  /** When false, skip the browser Pixel side (server-only). */
  alsoPixel?: boolean;
  /**
   * Disable the "auto-merge match keys from the signed-in user"
   * behaviour. Default is to merge — every event that fires while a
   * user is logged in carries email, external_id (uid), and the
   * name split from `displayName`, which is exactly what Meta's
   * EMQ dashboard asks for on InitiateCheckout / Purchase / etc.
   * Opt out only when you have a reason (e.g. a deliberately-anon
   * ViewContent before signup).
   */
  skipUserAutoMerge?: boolean;
}

export function newEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Narrow a raw survey-answer value into the `male` / `female` shape
 * Meta accepts for the `ge` user-data key. Anything else (skipped,
 * other, non-string) returns `undefined` so the field is omitted —
 * sending a value Meta doesn't recognise is worse than not sending
 * one (it counts against event_match_quality).
 *
 * Centralised here so every fireCapi call site does the narrowing
 * the same way; previously each paywall hand-rolled a guard inline.
 */
export function coerceCapiGender(
  value: unknown
): "male" | "female" | undefined {
  return value === "male" || value === "female" ? value : undefined;
}

/**
 * Fire a CAPI event (server) + the matching Pixel event (browser), both
 * tagged with the same event_id so Meta dedupes.
 *
 * Returns the event_id used — capture this if you need to reference the
 * same event later (e.g. fire StartTrial alongside Purchase with a
 * derived id).
 */
/**
 * Pull match-key fields from the currently-signed-in Firebase user.
 * Returns an empty object if nobody is signed in or if the SDK isn't
 * initialised yet. Used by `fireCapi` to auto-fill `em` / `external_id`
 * / `fn` / `ln` on every event a logged-in user triggers — without
 * forcing every call site to remember to pass them.
 *
 * EMQ payoff: Meta's dashboard cites a +43.6%–100% lift in additional
 * conversions reported when InitiateCheckout / Purchase carry these,
 * so the default-on behaviour is worth the small cost of importing
 * `getCurrentUser` here.
 */
function readUserMatchKeys(): Pick<
  FireOpts,
  "email" | "externalId" | "firstName" | "lastName"
> {
  try {
    const user = getCurrentUser();
    if (!user) return {};
    const displayName = (user.displayName ?? "").trim();
    const [first, ...rest] = displayName.split(/\s+/);
    return {
      email: user.email ?? undefined,
      externalId: user.uid,
      firstName: first || undefined,
      lastName: rest.length ? rest.join(" ") : undefined,
    };
  } catch {
    // Firebase not initialised (e.g. SSR / preview page) — soft-fail.
    return {};
  }
}

export function fireCapi(opts: FireOpts): string {
  const eventId = opts.eventId ?? newEventId();

  // ── Paid-only gate ──────────────────────────────────────────────
  // Only report to Meta for paid ad traffic. Organic / direct /
  // internal-team visits are skipped on BOTH sides (browser Pixel +
  // server CAPI) so internal testing never pollutes the Pixel or the
  // conversion signal the campaign optimises on. Returns the event_id
  // so callers that reference it still work; nothing is sent.
  if (!isPaidTraffic()) return eventId;

  const eventTime = Math.floor(Date.now() / 1000);

  // ── Auto-fill match keys from the signed-in user ────────────────
  // Explicit values in `opts` always win — we never overwrite a
  // deliberately-set address (e.g. one captured from the survey
  // before signup).
  const auto = opts.skipUserAutoMerge ? {} : readUserMatchKeys();
  const email = opts.email ?? auto.email;
  const firstName = opts.firstName ?? auto.firstName;
  const lastName = opts.lastName ?? auto.lastName;
  const externalId = opts.externalId ?? auto.externalId;

  // ── Browser-side Pixel (dedup pair) ────────────────────────────────
  if (opts.alsoPixel !== false) {
    const params: Record<string, unknown> = {};
    if (opts.value !== undefined) params.value = opts.value;
    if (opts.currency) params.currency = opts.currency.toUpperCase();
    if (opts.contentName) params.content_name = opts.contentName;
    if (opts.contentCategory) params.content_category = opts.contentCategory;
    if (opts.extraCustom) Object.assign(params, opts.extraCustom);
    fbqTrack(opts.eventName, params, { eventID: eventId });
  }

  // ── Server-side CAPI (fire-and-forget) ─────────────────────────────
  // userData carries RAW PII (Worker normalises + hashes via Meta's
  // SDK) plus the client-side fbp/fbc/IP captured by the param-builder
  // SDK (more accurate than what the Worker can see — IPv6 vs IPv4,
  // in-app browser click IDs). Server falls back to its own resolution
  // when fields are missing.
  const clientParams = readCapiClientParams();
  const payload = {
    events: [
      {
        eventName: opts.eventName,
        eventId,
        eventTime,
        sourceUrl:
          typeof window !== "undefined" ? window.location.href : "",
        actionSource: "website" as const,
        userData: {
          email,
          gender: opts.gender,
          phone: opts.phone,
          firstName,
          lastName,
          externalId,
          fbp: clientParams.fbp,
          fbc: clientParams.fbc,
          clientIpAddress: clientParams.clientIpAddress,
        },
        customData: {
          value: opts.value,
          currency: opts.currency,
          contentName: opts.contentName,
          contentCategory: opts.contentCategory,
          ...(opts.extraCustom ?? {}),
        },
      },
    ],
  };

  api
    .post<{ ok: boolean; status: number; eventsReceived?: number }>(
      "/api/capi",
      payload
    )
    .catch((err) => {
      // Marketing failures must never block the funnel. Log + carry on.
      console.warn("[capi] failed", opts.eventName, err);
    });

  return eventId;
}

const ONCE_KEY = (dedupKey: string) => `capi:once:${dedupKey}`;

/**
 * Fire a milestone event AT MOST ONCE per browser session, with a
 * stable, persisted `event_id`.
 *
 * Why this exists — Meta dedup health:
 *   A milestone like `Lead` fires from a screen-mount effect
 *   (variation-a's "calculating" step). That effect re-runs on
 *   browser back/forward navigation and on React Strict-Mode's
 *   double-mount in dev. Each plain `fireCapi` call mints a NEW
 *   `event_id`, so Meta receives several Lead events it treats as
 *   distinct — inflating counts and tanking the "deduplication keys
 *   match" health metric the Events Manager flags.
 *
 *   `fireCapiOnce` persists the chosen `event_id` under `dedupKey`
 *   in sessionStorage. The first call fires Pixel + CAPI with that
 *   id and records it. Subsequent calls in the same session no-op
 *   and return the stored id — so the Lead is counted exactly once,
 *   and the Pixel/CAPI pair always shares one dedup key.
 *
 * `dedupKey` should be stable for the logical event within a
 * session — e.g. `lead:${variation.slug}` so two different funnels
 * visited in one session each get their own Lead.
 */
export function fireCapiOnce(dedupKey: string, opts: FireOpts): string {
  // Paid-only gate (see fireCapi). Skip before touching the dedup key
  // so organic sessions don't leave any trace.
  if (!isPaidTraffic()) return opts.eventId ?? newEventId();

  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(ONCE_KEY(dedupKey));
  } catch {
    /* private mode / no storage — fall through and fire normally */
  }
  if (stored) return stored;

  const eventId = opts.eventId ?? newEventId();
  try {
    sessionStorage.setItem(ONCE_KEY(dedupKey), eventId);
  } catch {
    /* ignore — worst case the event fires again with the same flow */
  }
  return fireCapi({ ...opts, eventId });
}
